import { AggregationType } from '../setter/const';

export type GetArrayItem<T> = T extends Array<infer U> ? U : never;

/** 字段类型, 用于区分维度和指标 */
export enum FieldType {
  /** 聚合指标字段，可以被聚合 */
  Measure = 'measure',
  /** 聚合维度  */
  Dims = 'dimension',
  Default = 'default',
}

export interface MetaConfigRecordItem {
  id: string;
  name: string;
  settings?: {
    aliasName?: string;
    [key: string]: unknown;
    [AggregationType.Metrics]?: string;
    [AggregationType.Sorts]: string;
    sortConfig?: {
      open: boolean;
      sortType: string;
    };
  };
}
export interface MetaConfigItem {
  title: string;
  records: MetaConfigRecordItem[];
  type?: string | FieldType;
}

export interface BaseRequestConfig {
  __serviceType__?: string;
  __datasourceName__?: string;
  params?: Array<{ [key: string]: unknown }>;
  method?: string;
  serviceType?: string;
  contentType?: string;
  guid?: string; // 老版本数据集
  serviceCode?: string; // 新版本数据集
  url?: string;
  metaConfig?: Record<string, MetaConfigItem>;
  execute?: any;
  __extraConfig__?: { headers?: Record<string, unknown> | (() => Record<string, unknown>) };
}

export enum DataOriginType {
  dataeasy = 'dataeasy',
  request = 'request',
  static = 'static',
}

export interface RequestConfig {
  needSuccessToast?: boolean;
  isDesign?: boolean;
  recordDataSource?: Record<string, unknown>;
  state?: unknown;
  buttonConfig?: {
    options?: {
      requestConfig?: BaseRequestConfig;
      needConfirm?: boolean;
      confirmInfo?: {
        title?: unknown;
        content?: unknown;
      };
    };
  };
  handleParamsBeforeSend?: (data: Record<string, unknown>) => unknown;
  handleParams?: (data: Record<string, unknown>) => unknown;
  urlParamsDataSource?: unknown;
  onCancel?: () => void;
  extraParamList?: unknown;
  noNeedHandleResult?: boolean;
  getExtraParam?: () => void;
}

export interface FeatureConfig {
  name?: string;
  active?: boolean;
  options?: Record<string, unknown>;
}

export interface BaseLowCodeProps {
  _nodeId: string;
  _context: {
    state?: unknown;
  };
  isDesign?: boolean;
  filterConfig?: {
    _bindFilter?: unknown[];
  };
  forceRequest?: boolean;
  dataFrom?: {
    dataOrigin?: DataOriginType;
    requestConfig?: BaseRequestConfig;
    dataSource?: unknown;
  };
  sorts?: SortItem[] | null;
  currentPage?: number;
  pageSize?: number;
  chartStyle?: {
    height?: number;
    themeColor?: ThemeType;
  };
  aggregationConfig?: {
    aggregation?: {
      /** 聚合指标 */
      metrics?: Array<{
        name?: string;
        func?: string;
        alias?: string;
      }>;
      /** 聚合纬度 */
      dims?: string[];
    };
    sorts?: Array<{
      type?: string;
      name?: string;
    }>;
  } | null;
  _dataSource?: string;
  _dataSourceName?: string;
  omitInitRefreshData?: boolean;
  __featureConfig__?: FeatureConfig[];
}

export interface RealField {
  fieldName: string;
  aliasName?: string | undefined | ((...rest: any[]) => string);
}

export interface PagingProps {
  currentPage: number;
  pageSize: number;
  totalCount: number;
}

export interface SortItem {
  /** 排序字段 */
  resultName: string;
  /** 排序方式 */
  order?: 'DESC' | 'ASC';
}

export interface ThemeItem {
  label: string;
  color: string[];
}

export const defaultTheme = 'base10';

export type ThemeType =
  | 'base10'
  | 'base20'
  | 'coneblue'
  | 'iceblue'
  | 'conegreen'
  | 'deeplemon'
  | 'margueriteblue'
  | 'seagreen'
  | 'coral'
  | 'purpleplum'
  | 'seapink'
  | 'techgray';

export const themeMap: Record<ThemeType, ThemeItem> = {
  base10: {
    label: '基础10色',
    color: [
      '#355BD5',
      '#79C8F9',
      '#A85BEF',
      '#EEC31A',
      '#0ACEAC',
      '#258E4F',
      '#E98C4A',
      '#A12D9C',
      '#F08E9B',
      '#717498',
    ],
  },
  base20: {
    label: '扩展20色',
    color: [
      '#355BD5',
      '#B4C2EF',
      '#42B1F6',
      '#A3D9FB',
      '#0ACEAC',
      '#96FAE9',
      '#EEC31A',
      '#F9E8A8',
      '#A85BEF',
      '#D9B8F8',
      '#258E4F',
      '#AAE9C3',
      '#E98C4A',
      '#F5CCAE',
      '#A12D9C',
      '#E9B1E7',
      '#EA6173',
      '#F6BBC3',
      '#717498',
      '#CACBD8',
    ],
  },
  coneblue: {
    label: '顺序色-ConeBlue',
    color: ['#2140A1', '#284DC3', '#3D61D7', '#5F7DDE', '#8198E5', '#A3B4EC', '#C4CFF3', '#E6EBFA'],
  },
  iceblue: {
    label: '顺序色-IceBlue',
    color: ['#0973B5', '#0A8CDC', '#1BA1F4', '#42B1F6', '#69C1F8', '#90D1FA', '#B7E1FC', '#DEF1FD'],
  },
  conegreen: {
    label: '顺序色-ConeGreen',
    color: ['#056353', '#07987F', '#0ACEAC', '#1CF4CF', '#52F7DA', '#87F9E5', '#BDFCF1', '#E3FDF9'],
  },
  deeplemon: {
    label: '顺序色-DeepLemon',
    color: ['#675409', '#8D730A', '#C29D0E', '#EEC31A', '#F2D14E', '#F6DE82', '#FAECB7', '#FEF8E2'],
  },
  margueriteblue: {
    label: '顺序色-MargueriteBlue',
    color: ['#6B12BD', '#8016E2', '#9436EB', '#A85BEF', '#BC80F3', '#CFA5F6', '#E3CAFA', '#F7F0FE'],
  },
  seagreen: {
    label: '顺序色-SeaGreen',
    color: ['#196136', '#258E4F', '#31BA68', '#51D285', '#7EDDA4', '#AAE9C3', '#D7F5E3', '#ECFAF2'],
  },
  coral: {
    label: '顺序色-Coral',
    color: ['#6E350D', '#974912', '#C05D17', '#E47221', '#E98C4A', '#EEA673', '#F7D2B8', '#FCECE1'],
  },
  purpleplum: {
    label: '顺序色-PurplePlum',
    color: ['#81247D', '#A52EA0', '#C839C2', '#D25DCD', '#DC81D8', '#E6A5E3', '#F0C9EE', '#FAEDF9'],
  },
  seapink: {
    label: '顺序色-SeaPink',
    color: ['#B8182D', '#DC1D36', '#E53D53', '#EA6173', '#EF8593', '#F4A9B3', '#F8CDD3', '#FDF1F3'],
  },
  techgray: {
    label: '顺序色-TechGray',
    color: ['#4B4D67', '#5F6181', '#75779A', '#8F91AD', '#A9ABC0', '#C4C5D4', '#DEDFE7', '#F2F2F6'],
  },
};
